import React, { useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import NumberPad from './NumberPad'
import NumberPadV2 from './NumberPad/NumberPadV2'
import ChangeCalculatorField from './ChangeCalculatorField'
import Button from './Button'
import BackChevron from './BackChevron'
import useNumberPad from '../hooks/useNumberPad'
import NetworkIndicator from '../containers/NetworkIndicator'
import SegmentedControlV2 from '../components/SegmentedControl/SegmentedControl'

import { centsToDollars } from '../utils/formatters'

import styles from './ChangeCalculatorForm.module.scss'
import { ReactComponent as LoadingIcon } from '../assets/loading.svg'

const ChangeCalculatorForm = ({
  onComplete,
  onBack,
  pendingMutation,
  total,
  className,
  ...props
}) => {
  const classNames = cn(styles.changeCalculatorForm, className)
  const [completionPending, setCompletionPending] = useState(false)
  const [output, onInput, rawValue, isValid] = useNumberPad(
    0,
    centsToDollars,
    (value) => parseInt(value) >= total,
    6
  )

  const onTapNumberPad = (value) => {
    onInput(value)
  }

  const onTapShortcut = (value, isExact = false) => {
    if (isExact) {
      onInput(value, true)
    } else {
      onInput(parseInt(rawValue) + value, true)
    }
  }

  const clearInput = () => {
    onInput(0, true)
  }

  const handleOnComplete = () => {
    setCompletionPending(true)
    onComplete()
  }

  const orderLoading = pendingMutation !== undefined
  const change = Math.max(0, parseInt(rawValue) - parseInt(total))

  let changeLabel = isValid ? centsToDollars(change) : '--'
  let changeLabelState = 'default'
  let completeButton = (
    <button
      disabled={completionPending}
      onClick={handleOnComplete}
      className={styles.showReceiptButton}
    >
      {completionPending ? 'completing' : 'complete'}{' '}
      {completionPending ? <LoadingIcon className={styles.completionPending} /> : null}
    </button>
  )

  if (change > 0 || parseInt(rawValue) === total) {
    changeLabelState = 'prominent'
  }

  if (orderLoading || !isValid) completeButton = null
  const totalLabel = centsToDollars(parseInt(total))
  const totalElement = orderLoading ? (
    <LoadingIcon />
  ) : (
    <ChangeCalculatorField
      className={styles.field}
      label='total'
      text={orderLoading ? '-' : totalLabel}
      state='default'
    />
  )
  const [selectedAmountIndex, setSelectedAmountIndex] = useState(undefined)
  const isDoneButtonDisabled = !isValid || orderLoading
  const clientWidth = document?.body?.clientWidth

  const shortcutAmounts = [
    { label: '$1', value: 100 },
    { label: '$5', value: 500 },
    { label: '$20', value: 2000 },
    { label: 'Exact', value: 'exact' }
  ]

  const selectShortcutAmount = (selectedValue) => {
    const isExact = selectedValue === 'exact'
    const amount = isExact ? total : selectedValue
    setSelectedAmountIndex(selectedValue)
    onTapShortcut(amount, isExact)
  }
  return (
    <>
      {clientWidth >= 600 ? (
        <div {...props} className={classNames}>
          <BackChevron className={styles.backChevron} onClick={onBack} />
          <div className={styles.shortcutsContainer}>
            <Button className={styles.shortcutButton} onClick={() => onTapShortcut(100)}>
              $1
            </Button>
            <Button className={styles.shortcutButton} onClick={() => onTapShortcut(500)}>
              $5
            </Button>
            <Button className={styles.shortcutButton} onClick={() => onTapShortcut(1000)}>
              $10
            </Button>
            <Button className={styles.shortcutButton} onClick={() => onTapShortcut(2000)}>
              $20
            </Button>
            <Button className={styles.shortcutButton} onClick={() => onTapShortcut(5000)}>
              $50
            </Button>
            <Button className={styles.shortcutButton} onClick={() => onTapShortcut(total, true)}>
              exact
            </Button>
          </div>
          <div className={styles.numberPadSideBarContainer}>
            <div className={styles.numberPadContainer}>
              <NumberPad onTapButton={onTapNumberPad} className={styles.numberPad} />
              {completeButton}
            </div>
            <div className={styles.sideBar}>
              {totalElement}
              <ChangeCalculatorField
                className={styles.field}
                label='amount paid'
                text={output}
                state='active'
                canClear={rawValue > 0}
                onClear={clearInput}
              />
              <ChangeCalculatorField
                className={styles.field}
                label='change due'
                text={changeLabel}
                state={changeLabelState}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.changeCalculatorHeader}>
            <div className={styles.changeCalculatorHeaderContent}>
              <BackChevron onClick={onBack} backButtonText='' />
              <span className={styles.total}>{totalLabel}</span>
              <NetworkIndicator text='' />
            </div>
          </div>
          <div className={styles.changeCalculatorBody}>
            <div className={styles.changeCalculatorTransaction}>
              <div className={styles.changeCalculatorTransactionItem}>
                <span className={styles.label}>Amount Paid:</span>
                <span className={styles.amount}>{output}</span>
              </div>
              <div className={styles.changeCalculatorTransactionItem}>
                <span className={styles.label}>Change Due:</span>
                <span className={styles.amount}>{changeLabel}</span>
              </div>
            </div>
            <div className={styles.changeCalculatorShortcuts}>
              <SegmentedControlV2
                items={shortcutAmounts}
                onSelect={selectShortcutAmount}
                selectedValue={selectedAmountIndex}
              />
            </div>
            <NumberPadV2 onTapButton={onTapNumberPad} />
          </div>
          <div className={styles.changeCalculatorFooter}>
            <Button
              disabled={isDoneButtonDisabled}
              onClick={onComplete}
              className={styles.completeButton}
            >
              Done
            </Button>
          </div>
        </>
      )}
    </>
  )
}

ChangeCalculatorForm.defaultProps = {
  onComplete: () => {},
  total: 0,
  pendingMutation: undefined
}

ChangeCalculatorForm.propTypes = {
  onComplete: PropTypes.func,
  total: PropTypes.number,
  pendingMutations: PropTypes.string
}

export default ChangeCalculatorForm
